import React, { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  DropDownSelectorValues,
  ProjectStatus as ProjectStatusEnum,
  SubPage,
  Tab,
} from '@symfa-inc/providence-verizon-types';
import { Spin } from 'antd';
import { Equipment } from '@client/pages/engineering/components';
import { HttpService } from '@core/services';
import { EquipmentHttpService, ScopingHttpService } from '@core/services/http';
import {
  canUserEditByProjectStatusAndRoles,
  getProjectBundleIDParamFromUrl,
} from '@core/utils/methods';
import { EditablePage } from '@shared/components';
import { PrimaryButton } from '@shared/modules';
import { ScopingActions } from '@store/actions';
import {
  DDVSelectors,
  ProjectSelectors,
  ScopingSelectors,
  UserSelectors,
} from '@store/selectors';

type EquipmentTabProps = {
  isActive?: boolean;
};

export const EquipmentTab: FC<EquipmentTabProps> = ({ isActive }) => {
  const dispatch = useDispatch();
  const [projectBundleID] = getProjectBundleIDParamFromUrl();
  const isLoading = useSelector(
    ScopingSelectors.getTabLoadingState('equipment'),
  );
  const isDataLoaded = useSelector(
    ScopingSelectors.getTabLoadedState('equipment'),
  );
  const { equipmentTypeNames } = useSelector(
    ScopingSelectors.getScopingLeaseEquipmentData,
  );

  const { canCreateSOF } = useSelector(
    UserSelectors.getEngineeringScopingPermissions,
  );
  const projectType = useSelector(ScopingSelectors.getProjectType);
  const projectTypePermission = useSelector(
    UserSelectors.canEditProjectByProjectType(projectType),
  );
  const canEdit = useSelector(
    UserSelectors.canEdit(
      'engineering',
      SubPage.EngineeringScoping,
      Tab.Equipment,
    ),
  );

  const userRoles = useSelector(UserSelectors.getUserRoles);
  const projectStatusId = useSelector(ProjectSelectors.getProjectStatus);
  const projectStatus = useSelector(
    DDVSelectors.getCurrentDDVValueById<ProjectStatusEnum>(
      DropDownSelectorValues.ProjectStatus,
      projectStatusId,
    ),
  );

  useEffect(() => {
    if (isActive && !isDataLoaded && !isLoading) {
      dispatch(
        ScopingActions.setTabLoading.done({
          tabName: 'equipment',
          isLoading: true,
        }),
      );

      const loadEquipmentTab = async (): Promise<void> => {
        try {
          await HttpService.getHttpRequests(
            EquipmentHttpService,
          ).getEquipmentTypes();
          await HttpService.getHttpRequests(
            ScopingHttpService,
          ).getScopingEquipment(projectBundleID);
        } catch {
          dispatch(
            ScopingActions.setTabError.done({
              tabName: 'equipment',
              hasError: true,
            }),
          );
        }
      };

      loadEquipmentTab();
    }
  }, [dispatch, isActive, isDataLoaded, isLoading, projectBundleID]);

  useEffect(() => {
    if (isActive && isDataLoaded && equipmentTypeNames?.length) {
      HttpService.getHttpRequests(EquipmentHttpService).getEquipmentsByTypes(
        equipmentTypeNames,
      );
    }
  }, [equipmentTypeNames, isActive, isDataLoaded]);

  if (isLoading || !isDataLoaded) {
    return (
      <div className="prov-scoping-equipment-tab loading">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <EditablePage
      editableComponent={Equipment}
      canUserEdit={
        canEdit &&
        projectTypePermission &&
        canUserEditByProjectStatusAndRoles(projectStatus, userRoles)
      }
    >
      {canCreateSOF && projectTypePermission && (
        <PrimaryButton
          title="Create SOF"
          icon="+"
          target="_blank"
          href={`/engineering/sof-tool/${projectBundleID}/details/summary`}
        />
      )}
    </EditablePage>
  );
};
